import React, { memo, useMemo, useCallback } from 'react';
import { ModalContent, Modal, ModalHeader, ModalFooter, ModalAction } from '@core/components/Modal';
import { useBloc } from '@core/utils/bloc';
import { CopyBlockTemplateModalBloc } from './CopyBlockTemplateModalBloc';
import { formGroup, FormDataType, useFormConfig, formControl, useFormState } from '@core/utils/form';
import { FormInput } from '@core/components/form';
import { switchMap, tap } from 'rxjs/operators';
import { useModalInstance } from '@core/components/ModalStack';
import { of } from 'rxjs';
import { t } from 'ttag';
import { CourseBuilder } from '@modules/course/CourseBuilder/Blocks/types';
import { CourseBuilderBlockContext } from '../../../CourseBuilderContext';
import { CourseBuilderBlockFormData, CourseBuilderBlockForm } from '../../../CourseBuilderForm';
import './index.scss';

function defineCopyBlockTemplateForm() {
  return formGroup({
    title: formControl<string>({
      label: 'Title',
      required: true,
      initialValue: '',
    }),
  });
}
export type CopyBlockTemplateForm = ReturnType<typeof defineCopyBlockTemplateForm>;
export type CopyBlockTemplateFormData = FormDataType<typeof defineCopyBlockTemplateForm>;

export interface CopyBlockTemplateModalProps {
  id: string;
  blocks: CourseBuilderBlockFormData[];
  blockItems: CourseBuilderBlockForm[];
}

export const CopyBlockTemplateModal = memo((props: CopyBlockTemplateModalProps) => {
  const { id, blocks, blockItems } = props;

  const newBlocks = useMemo(() => {
    const selectedBlock = blocks.find(b => b.id === id)!;
    if (selectedBlock.isSection) {
      const nextSection = blocks.find(b => b.menuOrder > selectedBlock.menuOrder && b.isSection);
      const applicableBlocks = blocks.filter(b => b.menuOrder >= selectedBlock.menuOrder && b.menuOrder < (nextSection?.menuOrder || 999999));
      return applicableBlocks;
    }
    return blocks.filter(b => b.id === id)!;
  }, [blocks, id]);

  const newBlocksForm = useMemo(() => {
    const selectedBlock = blocks.find(b => b.id === id)!;
    if (selectedBlock.isSection) {
      const nextSection = blockItems.find(b => (b.controls.menuOrder.value || 0) > selectedBlock.menuOrder && b.controls.isSection.value);
      const applicableBlocks = blockItems.filter(
        b =>
          (b.controls.menuOrder.value || 0) >= selectedBlock.menuOrder &&
          (b.controls.menuOrder.value || 0) < (nextSection?.controls.menuOrder.value || 999999),
      );
      return applicableBlocks;
    }
    return blockItems.filter(b => b.value.id === id)!;
  }, [blockItems, blocks, id]);

  const bloc = useBloc(CopyBlockTemplateModalBloc);
  const form = useMemo(defineCopyBlockTemplateForm, []);
  const modal = useModalInstance();

  const onSubmit = useCallback(
    (data: CopyBlockTemplateFormData) => {
      return of(null).pipe(
        switchMap(() => {
          return bloc.copyBlockTemplate({ title: data.title, data: newBlocks });
        }),
        tap(() => modal.close()),
      );
    },
    [bloc, modal, newBlocks],
  );

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });
  if (!newBlocks) return null;

  return (
    <Modal maxWidth="xl">
      <ModalHeader title="Copy Block"></ModalHeader>
      <ModalContent>
        <FormInput control={form.controls.title} />
        {newBlocks.map((block, index) => {
          return <BlockItem key={block.id} block={block} blockItem={newBlocksForm[index]} />;
        })}
      </ModalContent>
      <ModalFooter>
        <ModalAction intent="primary" text={t`Save`} autoClose={false} loading={submitting} onClick={form.submit} />
      </ModalFooter>
    </Modal>
  );
});

export interface BlockItemProps {
  block: CourseBuilderBlockFormData;
  blockItem: CourseBuilderBlockForm;
}

export const BlockItem = memo((props: BlockItemProps) => {
  const { block, blockItem } = props;
  const config = CourseBuilder.BLOCK_CONFIGS[block.type];
  const handleRef = React.useRef<CourseBuilder.BlockHandle>(null);

  const { component: BlockComponent } = config;

  if (!config || !block.id) return null;

  return (
    <CourseBuilderBlockContext.Provider value={{ blockForm: blockItem, blockIndex: 0 }}>
      <BlockComponent id={block.id} variation={block.variation} editable={false} handlesRef={handleRef} />
    </CourseBuilderBlockContext.Provider>
  );
});
